<template>
    <div>
        <top-bar-main />

        <header class="col-lg-10 mx-auto hero d-flex align-items-center">
            <div class="col-lg-6 col-sm-9 mx-auto ms-lg-0">
                <h1 class="mb-4">The <em>Free Pension Calculator</em>. Save time, hassle, and money!</h1>
                <a href="/account/getstarted" class="btn btn-primary btn-lg">
                    Start your plan 
                    <i class="bi bi-arrow-right ms-2"></i>
                </a>
            </div>
        </header> 

        <section class="col-lg-10 mx-auto">
            <div class="row">
                <div class="col-lg-4 col-sm-9  mx-auto ms-lg-0">
                    <h2 class="text-center text-md-start">Our free independent pension calculator gives you everything you need to <em>plan when you can retire</em>.</h2>
                </div>
                <div class="col-lg-6 offset-lg-2 col-sm-9 mx-auto me-lg-0">
                    <ul class="list-group list-group-flush border-top border-bottom">
                        <li v-for="planningHighlight, i in planningHighlights" :key="i" class="list-group-item py-4 d-flex align-items-start align-items-lg-center">
                            <div class="icon d-flex align-items-center justify-content-center" :class="`icon-${i + 1}`">
                                <i class="bi" :class="planningHighlight.icon"></i>
                            </div>
                            <div class="ms-5">
                                <h3>{{ planningHighlight.title }}</h3>
                                <span>{{ planningHighlight.description }}</span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </section>

        <section class="bg-light row">
            <div class="col-xl-10 mx-auto d-xl-flex align-items-center">
                <div class="col-xl-5">
                    <ul class="list-group list-group-flush border-0">
                        <li v-for="appBullet, i in appBullets" :key="i" class="list-group-item pb-5 border-0 bg-light">
                            <div>
                                <h3><i class="bi bi-check-circle-fill text-success"></i> {{ appBullet.title }}</h3>
                                <span>{{ appBullet.description }}</span>
                            </div>
                        </li>
                    </ul>
                </div>
                <div class="col-xl-6 offset-xl-1 text-center">
                    <img src="/Content/images/guiide-screenshot.png" class="img-fluid shadow text-center" alt="Screenshot of the design page in the Guiide retirement planning tool" />
                </div>
            </div>
        </section>

        <section class="col-lg-10 mx-auto">
            <div class="col-lg-6 col-sm-9 mx-auto">
                <h2 class="text-center"><em>Make your plan a reality</em> as simply as possible with an integrated pension product linked directly to the plan you've built.</h2>
            </div>
            <div class="d-flex flex-wrap">
                <div v-for="integrationHighlight, i in integrationHighlights" :key="i" class="card card-product col-xl-4 col-md-6" :class="`card-product-${i}`">
                    <div class="card-body">
                        <p class="card-icon mx-auto"><i class="bi" :class="integrationHighlight.icon"></i></p>
                        <h3 class="card-title">{{ integrationHighlight.title }}</h3>
                        <p class="card-text"><span v-html="integrationHighlight.description"></span></p>
                    </div>
                </div>
            </div>
        </section>

        <section class="col-lg-10 mx-auto">
            <div class="row">
                <div class="col-lg-4 text-center text-lg-start">
                    <h2>Top {{ topQuestions.length }} Frequently <br /> Asked Questions</h2>
                </div>
                <div class="col-lg-6 offset-lg-2">
                    <div class="accordion accordion-flush" id="topFiveFaqs">
                        <div v-for="faq, i in topQuestions" :key="i" class="accordion-item">
                            <h2 class="accordion-header py-2">
                                <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" :data-bs-target="`#flush-collapse${i}`" aria-expanded="false" :aria-controls="`flush-collapse${i}`">
                                    {{ faq.question }}
                                </button>
                            </h2>
                            <div :id="`flush-collapse${i}`" class="accordion-collapse collapse" data-bs-parent="#topFiveFaqs">
                                <div v-html="faq.answer" class="accordion-body"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="col-lg-10 mx-auto">
            <div class="trust-pilot-reviews bg-gradient pt-4">
                <h3 class="mb-3">Find out what some of our users think of Guiide</h3>
                <div :key="trustpilotWidgetKey" class="trustpilot-widget" data-locale="en-GB" data-template-id="54ad5defc6454f065c28af8b" data-businessunit-id="5d68312aef620200012de4d6" data-style-height="240px" data-style-width="100%" data-theme="dark" data-stars="5" data-review-languages="en">
                    <a href="https://uk.trustpilot.com/review/guiide.co.uk" target="_blank" rel="noopener">Trustpilot</a>
                </div>
            </div>
        </section>

        <Footer></Footer>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import faqs from "@/guiide/ts/faq/faqs";
import { FAQ } from "@/guiide/ts/faq/faq";
import TitleItem from "@/guiide/ts/titleItem";
import { ensureScriptLoaded, ensureScriptUnloaded } from "@/guiide/ts/utilities";
import IconTitleItem from "@/guiide/ts/iconTitleItem";
import TopBarMain from "@/guiide/components/common/TopBarMain.vue";
import Footer from "@/guiide/components/common/Footer.vue";

const trustpilotWidgetKey = ref(0);

onMounted(() => {
    ensureTrustpilotDisplaysCorrectly();
});

const planningHighlights: Array<IconTitleItem> = [
    new IconTitleItem('Needs', 'Understand the after tax income you need in retirement and estimate for how long.', 'bi-crosshair'),
    new IconTitleItem('Build', 'Use everything you have to get what you need and avoid running out of money.', 'bi-calculator'),
    new IconTitleItem('Tax', 'Reduce the tax you will pay in retirement by maximising your use of your tax free allowance.', 'bi-search-heart'),
    new IconTitleItem('Test', 'What if things don\'t go as expected? Stress test your plan.', 'bi-wind'),
    new IconTitleItem('Track', 'Easily keep an eye on things and make any adjustments needed.', 'bi-eye'),
];

const appBullets: Array<TitleItem> = [
    new TitleItem('Finish a plan in minutes', 'Our easy to use system can help you quickly develop a retirement plan in a few minutes.'),
    new TitleItem('Customise your needs', 'Create an income plan which can be as bespoke as you like.'),
    new TitleItem('Use your flexible parts right', 'To build your plan we take all your fixed income and then use the flexible bits, pension and savings pot withdrawals to get what you need. We split the withdrawals from both to make these more tax efficient where we can. This gives you a clear answer to the difficult question of how much you need to withdraw from each pot every year during your retirement.'),
    new TitleItem('Your priorities', 'Let Guiide do the heavy lifting on retirement calculations so you can focus on the things that matter to you.')
];

const integrationHighlights: Array<IconTitleItem> = [
    new IconTitleItem('Coming Soon!', 'Make your plan a reality with pension pot withdrawals paid automatically like a wage and investment splits tailored to your specific plan and income needs. A new integrated product will provide all this. If interested, you can register for more details now, once you have built a plan.', 'bi-stopwatch'),
    new IconTitleItem('Planned Payments', 'Your Guiide drawdown plan is directly integrated with the pension provider, meaning they just pay the withdrawals you want in our plan until you change them making receiving the monthly payments you want effortless.', 'bi-calendar-date-fill'),
    new IconTitleItem('Updates', 'Guiide automatically holds the latest value of your pension pot allowing you to confidently revise your plan each April should you wish.', 'bi-hourglass-split'),
    new IconTitleItem('Matched Investment Strategy', 'Your Guiide drawdown plan means the investment manager knows how you expect to take your money. This means they can use assets which match these expected payments a lot better than usual default funds. You can choose how long you would like your payments matched for, leaving the rest in funds designed to optimise growth over the longer term. It\'s all done for you.', 'bi-graph-up-arrow'),
    new IconTitleItem('Ad-hoc Payments', 'There\'s no problem getting one-off withdrawals after you retire for life\'s unexpected events. You can even see the effect on your long term plan before deciding whether to take one. The same goes for any ad-hoc added contributions you want to make.', 'bi-cash-coin'),
    new IconTitleItem('Plan as a couple ', 'Once the product is launched you can plan for yourself or as a couple, using your partners tax free allowance also.', 'bi-people-fill')
];

const topQuestions: Array<FAQ> = faqs.filter(f => f.topQuestion);

function ensureTrustpilotDisplaysCorrectly() {
    const trustpilotSrc = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";

    ensureScriptUnloaded(trustpilotSrc);

    trustpilotWidgetKey.value += 1;

    ensureScriptLoaded(trustpilotSrc, () => {
        if ((window as any).Trustpilot) {
            (window as any).Trustpilot.loadFromElement(document.querySelector('.trustpilot-widget'));
        }
    });
}

</script>

<style scoped>
    h1 {
        font-size: 3.7rem;
    }

    @media (max-width: 991px) {
        h1 {
            font-size: 2.8rem;
        }
    }

    h1 > em, 
    h2 > em {
        font-weight: 600;
        font-style: normal;
        color: #004687;
    }

    h2 {
        margin-bottom: 50px;
        line-height: 1.4;
    }

    h3 {
        color: #004687;
    }

    .btn-lg {
        padding: 1rem 2rem;
    }

    .btn-primary:hover {
        opacity: .7;
    }

    .hero {
        background: url("https://d9hhrg4mnvzow.cloudfront.net/try.guiide.co.uk/simple/83cbd7c2-robot-with-paint-background_10000000ff0id00000n028.png") no-repeat right;
        background-position-y: bottom 80px;
        height: 86vh;
    }

    @media (max-width: 991px) {
        .hero {
            background: none;
        }
    }

    section {
        padding: 10rem 0;
    }

    .features {
        background: url("/Content/images/guiide-screenshot.png") 0 0 no-repeat;
        background-size: contain;
        height: 745px;
        border-radius: 9px;
    }

    .icon {
        padding: 15px 30px;
        border-radius: 60px;
    }

    .card-product {
        background-color: #fff;
        border: none;
        padding: 25px;
    }

    .card-product .card-icon {
        background-color: #44bf21;
        color: #fff !important;
        display: inline-block;
        border-radius: 40px;
        padding: 18px 24px;
        font-size: 28px !important;
        text-align: center;
    }

    .card-product .card-body {
        background-color: #dddddd;
        padding: 35px;
        font-size: 20px;
        border-radius: 18px;
    }

    .card-product h3 {
        color: #212529;
    }

    .card-product-0 .card-body {
        background-color: #efefef;
    }

    .card-product .card-title {
        font-size: 1.6rem;
        font-weight: 600;
        margin-bottom: 20px;
    }

    .card-product .card-text {
        font-size: 20px;
    }

    @media (max-width: 991px) {
        .icon {
            padding: 7px 15px;
            border-radius: 40px;
        }

        .list-group-item .ms-5 {
            margin-left: 1.5rem !important;
        }
    }

    .icon > i {
        font-size: 60px;
    }

    @media (max-width: 991px) {
        .icon > i {
            font-size: 30px;
        }
    }

    .icon-1 {
        background-color: #976aba;
        color: #fff;
    }

    .icon-2 {
        background-color: rgb(236, 227, 62);
        color: #fff;
    }

    .icon-3 {
        background-color: #e0bd28;
        color: #fff;
    }

    .icon-4 {
        background-color: lightskyblue;
        color: #fff;
    }

    .icon-5 {
        background-color: rgb(85, 188, 85);
        color: #fff;
    }

    .trust-pilot-reviews {
        background-color: silver;
        padding: 15px;
        margin-top: 25px;
        border-radius: 8px;
    }

    .trust-pilot-reviews h3 {
        color: #fff;
        text-align: center;
    }

    .accordion-header {
        margin-bottom: 0;
    }

    .accordion-button {
        color: #004687;
        font-weight: 600;
    }

    .accordion-button,
    .accordion-body {
        font-size: 1.4rem;
    }
</style>
